import React, { useContext, useEffect, useState } from "react";
import { Modal, Notification, STATUS_TYPES, TextForm, generateResolver, productRestriction, PRODUCT_PERMISSIONS } from "dyl-components";
import { Button, Form, Grid, Icon, Popup, Segment } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";

import text_templatesActions from "actions/text_templates";

import "./index.scss";
import { FormProvider, useController, useForm } from "react-hook-form";
import { FileUtils, PhoneUtil } from "utils";
import uploadActions from "actions/upload";
import { convertSenderVariables } from "utils/TextFormUtils";
import { FILE_CATEGORIES } from "utils/FileUtils";
import CategorizedTemplates from "shared/NewTasksTab/subcomponents/CategorizedTemplates";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";
import { TextingComponentContext } from "shared/context/TextingComponentProvider";
import Recipients from "./Recipients";
import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import textActions from "actions/text";

const TextFormContainer = ({ setIsTextFormDirty, campaign_id, onMessageSend }) => {
    const dispatch = useDispatch();
    const [totalContactCount, setTotalContactCount] = useState(null);
    const isSendingText = useSelector(state => state.text.isStartingBulkTexting || state.upload.isUploadingFile);
    const brandOptOutStatus = useSelector(state => state.tcr.brandOptOutStatus);
    const textCampaignContactIds = useSelector(state => state.campaign_contacts.textCampaignContactIds);
    const userProfile = useSelector(state => state.user.userProfile);
    const textCampaignContactsCount = useSelector(state => state.campaign_contacts.textCampaignContactsCount || 0);
    const textPhones = useSelector(state => state.text.phones?.map(phone => ({
        key: phone,
        value: phone,
        text: PhoneUtil.formatPhoneNumber(phone)
    })) || []);
    const productPermissions = useSelector(
        (state) => state.subscription.productPermissions
    );
    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);
    const methods = useForm({
        defaultValues: {
            message: '',
            attachments: [],
            include_opt_out: false,
            from: textPhones[0]?.value || ''
        },
        mode: 'onChange',
        resolver: generateResolver({
            attachments: FileUtils.getFilesizeValidator(FILE_CATEGORIES.TEXT),
        })
    });
    const { control, formState: { isValid, isDirty }, handleSubmit } = methods;
    const { field: fromField } = useController({
        name: 'from',
        control
    })
    const {
        field: messageField
    } = useController({
        name: 'message',
        control
    });
    const {
        field: attachmentsField
    } = useController({
        name: 'attachments',
        control
    });

    useEffect(()=>{
        setIsTextFormDirty(methods.formState.isDirty)
    },[methods.formState.isDirty, setIsTextFormDirty])

    useEffect(()=>{
        if(textCampaignContactsCount && totalContactCount === null){
            setTotalContactCount(textCampaignContactsCount || 0)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[textCampaignContactsCount])

    const handleSelectTemplateChange = (value) => {
        if (value) {
            dispatch(text_templatesActions.getTemplate(value))
                .then(({ message, attachments }) => {
                    const formattedMessage = convertSenderVariables(message, userProfile);
                    messageField.onChange({
                        target: {
                            name: messageField.name,
                            value: formattedMessage || ''
                        }
                    });
                    const mappedAttachments = (attachments?.data || []).map(att => ({
                        id: att.file_id,
                        name: att.name,
                        size: att.size,
                        url: att.url,
                        path: att.name
                    }));

                    attachmentsField.onChange({
                        target: {
                            name: attachmentsField.name,
                            value: mappedAttachments
                        }
                    });
                }).catch(error => {
                    console.log(error);
                });
        }
    };

    const [selectedContacts,,areContactsInAllPagesSelected] = useContext(BulkActionsContext)
    const handleSendText = async (data) => {
        try {
            const contact_id = areContactsInAllPagesSelected ? textCampaignContactIds : selectedContacts;
            const attachments = data.attachments || [];
            const templateAttachments = attachments.filter(att => att.id).map(att => att.id);
            const newAttachments = attachments.filter(att => !att.id);
            const uploadedAttachments = await (async () => {
                if (newAttachments.length) {
                    return dispatch(uploadActions.uploadFiles(newAttachments, 'text', []));
                }
                return [];
            })();
            await dispatch(textActions.startBulkTexting({
                contact_id: contact_id,
                phone: `${data.from}`,
                message: data.message,
                opt_out_message: data.include_opt_out || false,
                attachment: [...templateAttachments, ...uploadedAttachments],
            }, null, campaign_id))
            onMessageSend()
            Notification.alert('Sucessfully sent texts!', STATUS_TYPES.SUCCESS);
        } catch (error) {
            console.log(error);
            Notification.alert('Failed to send texts', STATUS_TYPES.ERROR);
        }
    };

    const isReadingCategorizedTemplates = useSelector(state => state.template_category.isReadingCategorizedTemplates);
    return (
        <>
            <Modal.Content>
                <FormProvider {...methods}>
                    <Form>
                        <Grid columns='equal' celled className='TextCampaignModal__form'>
                            <Grid.Column className="TextCampaignModal__recipients">
                                <Recipients textCampaignContactsCount={textCampaignContactsCount || 0} totalContactCount={totalContactCount || 0}/>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Select 
                                    label={{
                                        children: (
                                            <span>
                                                From
                                                <Popup
                                                    trigger={<Icon style={{ float: 'right', marginTop: '0.25em' }} name='fas fa-info-circle' color='primary' />}
                                                    content='Only registered TCR numbers will appear'
                                                    inverted
                                                    position='top right' 
                                                    offset={[10, 0]}
                                                />
                                            </span>
                                        )
                                    }}
                                    name={fromField.name}
                                    options={textPhones}
                                    onChange={(_, { value }) => fromField.onChange(value)}
                                    value={fromField.value}
                                    required
                                    selectOnBlur={false}
                                    width={8}
                                />
                                <TextForm
                                    textFieldName={messageField.name}
                                    templateSelector={(
                                        <CategorizedTemplates
                                            onSelectTemplate={handleSelectTemplateChange}
                                            isReadingCategorizedTemplates={isReadingCategorizedTemplates}
                                            type='text'
                                        />
                                    )}
                                    rows={7}
                                    loading={isSendingText}
                                    attachmentsFieldName={attachmentsField.name}
                                    optOutFieldName='include_opt_out'
                                    disableOptOut={!brandOptOutStatus?.use_opt_out}
                                    infoIconMessage={`The message ‘${brandOptOutStatus?.opt_out_message}’ will be added at the end of your actual message. This affects the total character count below.`}
                                    additionalCharacters={brandOptOutStatus?.opt_out_message?.length}
                                    onReplaceVariable={(variable) => {
                                        if (variable.includes('My')) {
                                            return convertSenderVariables(variable, userProfile)
                                        }
                                        return variable;
                                    }}
                                />
                                <Button
                                    primary
                                    onClick={handleSubmit(handleSendText)}
                                    loading={isSendingText}
                                    disabled={((!isValid || !isDirty)) || isSendingText || !hasText || selectedContacts.length === 0}
                                    style={{ float: 'right' }}
                                >
                                    Send
                                </Button>
                            </Grid.Column>
                        </Grid>
                    </Form>
                </FormProvider>
            </Modal.Content>
        </>
    );
}

const TextCampaignModal = ({ open, onClose, onMessageSend, campaign_id }) => {
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const { isTextFormDirty , setIsTextFormDirty } = useContext(TextingComponentContext)

    const isReading = useSelector(state => state.user.isReadingUser);
    return (
        <>
            <Modal
                open={open}
                className="TextCampaignModal"
                onClose={() => {isTextFormDirty ? setIsConfirmationOpen(true) : onClose() }}
                animated={false}
                size='big'
            >
                <Modal.Header>Text Campaign</Modal.Header>
                {isReading ? <Segment basic loading></Segment> : <TextFormContainer campaign_id={campaign_id} setIsTextFormDirty={setIsTextFormDirty} onMessageSend={onMessageSend} />}
            </Modal>
             <FullScreenModalPopUp
                header={"Text will not be saved."}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onClose();
                    setIsConfirmationOpen(false);
                    setIsTextFormDirty(false)
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </>
    );
};


export default TextCampaignModal;
